import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import { useVisualization } from '@/context';
import type { MeasurementResult } from '@/visualization/domain/sizing/measurement-result';
import type { ISizingTemplate, IKeypointDefinition } from '@/visualization/domain/sizing/templates/types';
import {
    SizingStateService,
    type DiameterGroup,
    type CustomMeasurementDraft,
    type DraftKind,
    type DraftSlot,
} from '@/visualization/services/sizing/sizing-state-service';

// ============================================================================
// Types
// ============================================================================

export type { DiameterGroup } from '@/visualization/services/sizing/sizing-state-service';
export type {
    CustomMeasurementDraft,
    DraftKind,
    DraftSlot,
} from '@/visualization/services/sizing/sizing-state-service';

export interface MeasurementsByCategory {
    lengths: MeasurementResult[];
    diameters: MeasurementResult[];
    angles: MeasurementResult[];
    derived: MeasurementResult[];
}

export interface SizingStateSnapshot {
    template: ISizingTemplate;
    editableKeypoints: IKeypointDefinition[];
    measurements: MeasurementsByCategory;
    diameterGroups: DiameterGroup[];
    minMaxDiameters: MeasurementResult[];
    customDiameters: MeasurementResult[];
    customLengths: MeasurementResult[];
    customAngles: MeasurementResult[];
    selectedMeasurementId: string | null;
    customMeasurementIds: ReadonlySet<string>;
    draft: CustomMeasurementDraft | null;
    isDraftComplete: boolean;
    canCommitDraft: boolean;
    selectMeasurement: (id: string) => void;
    deselectMeasurement: () => void;
    addOffsetDiameter: (keypointId: string, offsetMm: number) => void;
    removeOffsetDiameter: (offsetId: string) => void;
    removeCustomMeasurement: (id: string) => void;
    renameCustomMeasurement: (id: string, newName: string) => void;
    // Facade actions (bridge to CurrentLocationService via SizingStateService)
    moveKeypointToCurrentLocation: (keypointId: string) => boolean;
    addCustomDiameterAtCurrentLocation: () => string | null;
    relocateMeasurementToCurrentLocation: (measurementId: string) => boolean;
    navigateToNode: (nodeId: number) => void;
    navigateToMeasurement: (measurementId: string) => void;
    // Custom-measurement draft actions
    beginCustomDraft: (kind: DraftKind) => void;
    setDraftPointToCurrentLocation: (slot: DraftSlot) => void;
    cancelDraft: () => void;
    reassignCustomMeasurementPoint: (measurementId: string, slot: DraftSlot) => boolean;
}

// ============================================================================
// Hook
// ============================================================================

export function useSizingState(): SizingStateSnapshot | null {
    const { getServiceManager } = useVisualization();
    const [snapshot, setSnapshot] = useState<SizingStateSnapshot | null>(null);
    const serviceRef = useRef<SizingStateService | null>(null);

    /** Stable action callbacks that delegate to the current service ref. */
    const actions = useMemo(
        () => ({
            selectMeasurement: (id: string) => serviceRef.current?.selectMeasurement(id),
            deselectMeasurement: () => serviceRef.current?.deselectMeasurement(),
            addOffsetDiameter: (keypointId: string, offsetMm: number) =>
                serviceRef.current?.addOffsetDiameter(keypointId, offsetMm),
            removeOffsetDiameter: (offsetId: string) => serviceRef.current?.removeOffsetDiameter(offsetId),
            removeCustomMeasurement: (id: string) => serviceRef.current?.removeCustomMeasurement(id),
            renameCustomMeasurement: (id: string, newName: string) =>
                serviceRef.current?.renameCustomMeasurement(id, newName),
            // Facade actions
            moveKeypointToCurrentLocation: (keypointId: string) =>
                serviceRef.current?.moveKeypointToCurrentLocation(keypointId) ?? false,
            addCustomDiameterAtCurrentLocation: () =>
                serviceRef.current?.addCustomDiameterAtCurrentLocation() ?? null,
            relocateMeasurementToCurrentLocation: (measurementId: string) =>
                serviceRef.current?.relocateMeasurementToCurrentLocation(measurementId) ?? false,
            navigateToNode: (nodeId: number) => serviceRef.current?.navigateToNode(nodeId),
            navigateToMeasurement: (measurementId: string) =>
                serviceRef.current?.navigateToMeasurement(measurementId),
            // Custom-measurement draft actions
            beginCustomDraft: (kind: DraftKind) => serviceRef.current?.beginCustomDraft(kind),
            setDraftPointToCurrentLocation: (slot: DraftSlot) =>
                serviceRef.current?.setDraftPointToCurrentLocation(slot),
            cancelDraft: () => serviceRef.current?.cancelDraft(),
            reassignCustomMeasurementPoint: (measurementId: string, slot: DraftSlot) =>
                serviceRef.current?.reassignCustomMeasurementPoint(measurementId, slot) ?? false,
        }),
        []
    );

    const buildSnapshot = useCallback(() => {
        const service = serviceRef.current;
        if (!service) return;

        const template = service.getTemplate();
        if (!template) {
            setSnapshot(null);
            return;
        }

        const measurements = service.getMeasurementsByCategory();
        const { groups, minMax, custom } = service.getDiameterGroups();
        const customByCategory = service.getCustomMeasurementsByCategory();

        setSnapshot({
            template,
            editableKeypoints: service.getEditableKeypoints(),
            measurements,
            diameterGroups: groups,
            minMaxDiameters: minMax,
            customDiameters: custom,
            customLengths: customByCategory.lengths,
            customAngles: customByCategory.angles,
            selectedMeasurementId: service.getSelectedMeasurementId(),
            customMeasurementIds: service.getCustomMeasurementIds(),
            draft: service.getDraft(),
            isDraftComplete: service.isDraftComplete(),
            canCommitDraft: service.canCommitDraft(),
            ...actions,
        });
    }, [actions]);

    useEffect(() => {
        const sm = getServiceManager();
        const service = sm.getService<SizingStateService>(SizingStateService.SERVICE_NAME);
        serviceRef.current = service;

        // If already initialized, build immediately
        if (service.getTemplate()) {
            buildSnapshot();
        }

        const unsubscribers = [
            service.subscribe(service.EVENTS.SIZING_INITIALIZED, buildSnapshot),
            service.subscribe(service.EVENTS.MEASUREMENTS_UPDATED, buildSnapshot),
            service.subscribe(service.EVENTS.KEYPOINT_MOVED, buildSnapshot),
            service.subscribe(service.EVENTS.DIAMETER_EDITED, buildSnapshot),
            service.subscribe(service.EVENTS.DIAMETER_RESTORED, buildSnapshot),
            service.subscribe(service.EVENTS.MEASUREMENT_RELOCATED, buildSnapshot),
            service.subscribe(service.EVENTS.CUSTOM_MEASUREMENT_ADDED, buildSnapshot),
            service.subscribe(service.EVENTS.CUSTOM_MEASUREMENT_REMOVED, buildSnapshot),
            service.subscribe(service.EVENTS.CUSTOM_MEASUREMENT_RENAMED, buildSnapshot),
            service.subscribe(service.EVENTS.OFFSET_ADDED, buildSnapshot),
            service.subscribe(service.EVENTS.OFFSET_REMOVED, buildSnapshot),
            service.subscribe(service.EVENTS.MEASUREMENT_SELECTION_CHANGED, buildSnapshot),
            service.subscribe(service.EVENTS.CUSTOM_MEASUREMENT_DRAFT_CHANGED, buildSnapshot),
        ];

        return () => {
            serviceRef.current = null;
            unsubscribers.forEach((unsub) => unsub());
        };
    }, [getServiceManager, buildSnapshot]);

    return snapshot;
}
